import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiSettings, FiX } from "react-icons/fi";
import { useSchoolName, setSchoolName as saveSchoolName } from "./schoolNameStore";

// ─────────────────────────────────────────────────────────────────────────────
//  ADMIN DEFAULT (hardcoded — unchanged)
// ─────────────────────────────────────────────────────────────────────────────
import { BookOpen, X, ArrowLeft, GraduationCap, Crown, KeyRound, School, CheckCircle2, XCircle, Dna, Monitor, Palette, BarChart3, EyeOff, Eye } from "lucide-react";
const ADMIN_DEFAULT = {
  userId: "ADM-0301",
  password: "admin@123"
};

// ─────────────────────────────────────────────────────────────────────────────
//  ROLE CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const roleConfig = {
  principal: {
    label: "Principal ID",
    placeholder: "Enter your username",
    info: "Full administrative access",
    icon: <Crown className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    title: "Principal"
  },
  staff: {
    label: "Staff ID / Username",
    placeholder: "e.g. STF-1042",
    info: "Academic & classroom access",
    icon: <BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    title: "Staff"
  },
  admin: {
    label: "Admin ID / Username",
    placeholder: "ADM-0301",
    info: "Records & operations access",
    icon: "",
    title: "Office Admin"
  }
};

// Icon badge background per role — matches the light, flat badge look
const ROLE_ICON_BG = {
  principal: "#F3D583",
  staff: "#DCE9D6",
  admin: "#DCE2E8"
};

// Gold accent used for the primary Sign In button + LogBook icon chip
const GOLD_GRADIENT = "linear-gradient(135deg, #F3D583, #E8C468)";

// ─────────────────────────────────────────────────────────────────────────────
//  CLASS STRUCTURE
// ─────────────────────────────────────────────────────────────────────────────
const HS_GROUPS = ["Bio Maths", "Computer Science", "Art & Science", "Commerce"];
const CLASS_STRUCTURE = [{
  label: "LKG",
  sections: ["A", "B", "C"],
  isHS: false
}, {
  label: "UKG",
  sections: ["A", "B", "C"],
  isHS: false
}, ...Array.from({
  length: 10
}, (_, i) => ({
  label: `Class ${i + 1}`,
  sections: ["A", "B", "C"],
  isHS: false
})), {
  label: "Class 11",
  isHS: true,
  groups: HS_GROUPS.map(g => ({
    group: g,
    sections: ["A", "B"]
  }))
}, {
  label: "Class 12",
  isHS: true,
  groups: HS_GROUPS.map(g => ({
    group: g,
    sections: ["A", "B"]
  }))
}];
const classBtnStyle = active => ({
  background: active ? "rgba(26,77,46,0.3)" : "rgba(255,255,255,0.08)",
  border: active ? "1.5px solid #1A4D2E" : "1px solid rgba(255,255,255,0.15)",
  borderRadius: 10,
  padding: "10px 6px",
  color: "#E8DFCA",
  fontSize: 13,
  fontWeight: active ? 700 : 400,
  cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
  transition: "all 0.15s"
});

// ─────────────────────────────────────────────────────────────────────────────
//  WAITING SCREEN
// ─────────────────────────────────────────────────────────────────────────────
function WaitingApproval({
  staffUserId,
  className,
  onApproved,
  onCancel
}) {
  const [dots, setDots] = useState(".");
  const [status, setStatus] = useState("pending");
  const pollRef = useRef(null);
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? "." : d + "."), 600);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/logbook-approvals/status?staffUserId=${encodeURIComponent(staffUserId)}&className=${encodeURIComponent(className)}`);
        const data = await res.json();
        if (data.status === "approved") {
          setStatus("approved");
          clearInterval(pollRef.current);
          setTimeout(() => onApproved(), 1200);
        } else if (data.status === "rejected") {
          setStatus("rejected");
          clearInterval(pollRef.current);
        }
      } catch {}
    };
    pollRef.current = setInterval(poll, 3000);
    poll();
    return () => clearInterval(pollRef.current);
  }, [staffUserId, className, onApproved]);
  const colorMap = {
    pending: {
      bg: "rgba(255,255,255,0.15)",
      border: "rgba(255,255,255,0.4)",
      icon: "⏳"
    },
    approved: {
      bg: "rgba(255,255,255,0.18)",
      border: "rgba(255,255,255,0.45)",
      icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
    },
    rejected: {
      bg: "rgba(0,0,0,0.15)",
      border: "rgba(0,0,0,0.3)",
      icon: <XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />
    }
  };
  const c = colorMap[status];
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "#F5EFE6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans',sans-serif",
    padding: 16
  }}>
      <div style={{
      background: "#4F6F52",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 24,
      boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
      width: "100%",
      maxWidth: 400,
      padding: "36px 32px",
      textAlign: "center"
    }}>
        <div style={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        margin: "0 auto 20px",
        background: c.bg,
        border: `2px solid ${c.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 32
      }}>{c.icon}</div>

        {status === "pending" && <>
            <div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 18,
          marginBottom: 8
        }}>Waiting for Approval{dots}</div>
            <div style={{
          color: "rgba(255,255,255,0.8)",
          fontSize: 13,
          lineHeight: 1.6,
          marginBottom: 24
        }}>
              Your request to access <span style={{
            color: "#fff",
            fontWeight: 700
          }}>{className}</span> LogBook has been sent to the <span style={{
            color: "#fff",
            fontWeight: 700
          }}>Class Advisor</span>.
              <br /><br />Please wait while they approve your access.
            </div>
            <div style={{
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 12,
          padding: "12px 16px",
          marginBottom: 24
        }}>
              <div style={{
            color: "rgba(255,255,255,0.65)",
            fontSize: 11,
            marginBottom: 4
          }}>CLASS</div>
              <div style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 14
          }}>{className}</div>
            </div>
            <button onClick={onCancel} style={{
          width: "100%",
          padding: "12px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff",
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 13,
          cursor: "pointer"
        }}>Cancel Request</button>
          </>}
        {status === "approved" && <>
            <div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 18,
          marginBottom: 8
        }}>Access Approved!</div>
            <div style={{
          color: "rgba(255,255,255,0.8)",
          fontSize: 13
        }}>Redirecting you to LogBook{dots}</div>
          </>}
        {status === "rejected" && <>
            <div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 18,
          marginBottom: 8
        }}>Access Rejected</div>
            <div style={{
          color: "rgba(255,255,255,0.8)",
          fontSize: 13,
          marginBottom: 24,
          lineHeight: 1.6
        }}>
              The class advisor has rejected your LogBook access request for <span style={{
            color: "#fff",
            fontWeight: 700
          }}>{className}</span>.<br />Please contact your class advisor.
            </div>
            <button onClick={onCancel} style={{
          width: "100%",
          padding: "12px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.3)",
          color: "#fff",
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 13,
          cursor: "pointer",
          fontWeight: 600
        }}>Back to Login</button>
          </>}
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  LOGBOOK MODAL
// ─────────────────────────────────────────────────────────────────────────────
function LogBookModal({
  onClose,
  onLogin
}) {
  const [lbUserId, setLbUserId] = useState("");
  const [lbPassword, setLbPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({
    userId: "",
    password: "",
    class: ""
  });
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [advisorName, setAdvisorName] = useState("");
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [selectorStep, setSelectorStep] = useState("class");
  const [waitingApproval, setWaitingApproval] = useState(false);
  const [approvedStaffData, setApprovedStaffData] = useState(null);
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };
  const buildClassName = () => {
    if (!selectedClass || !selectedSection) return null;
    if (selectedClass.isHS) {
      if (!selectedGroup) return null;
      return `${selectedClass.label} ${selectedGroup}-${selectedSection}`;
    }
    return `${selectedClass.label}-${selectedSection}`;
  };
  const displayLabel = buildClassName() || "Click to select class & section";
  const fetchAdvisor = async className => {
    setAdvisorLoading(true);
    setAdvisorName("");
    try {
      const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/class-advisors/${encodeURIComponent(className)}`);
      if (res.ok) {
        const data = await res.json();
        setAdvisorName(data.staffId?.name || "");
      }
    } catch {} finally {
      setAdvisorLoading(false);
    }
  };
  const handleSectionPick = sec => {
    setSelectedSection(sec);
    setShowSelector(false);
    setSelectorStep("class");
    const cn = selectedClass.isHS ? `${selectedClass.label} ${selectedGroup}-${sec}` : `${selectedClass.label}-${sec}`;
    fetchAdvisor(cn);
    setErrors(p => ({
      ...p,
      class: ""
    }));
  };
  const resetSelector = () => {
    setSelectedClass(null);
    setSelectedGroup(null);
    setSelectedSection(null);
    setAdvisorName("");
    setSelectorStep("class");
  };
  const groupIcon = g => g === "Bio Maths" ? <Dna className="inline w-[1em] h-[1em] align-[-0.125em]" /> : g === "Computer Science" ? <Monitor className="inline w-[1em] h-[1em] align-[-0.125em]" /> : g === "Art & Science" ? <Palette className="inline w-[1em] h-[1em] align-[-0.125em]" /> : <BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" />;
  const handleLogBookLogin = async () => {
    const className = buildClassName();
    if (!className) {
      setErrors(p => ({
        ...p,
        class: "Please select a class and section"
      }));
      triggerShake();
      return;
    }
    if (!lbUserId.trim()) {
      setErrors(p => ({
        ...p,
        userId: "Please enter your Staff ID",
        password: ""
      }));
      triggerShake();
      return;
    }
    if (!lbPassword.trim()) {
      setErrors(p => ({
        ...p,
        userId: "",
        password: "Please enter your password"
      }));
      triggerShake();
      return;
    }
    setLoading(true);
    try {
      const authRes = await fetch("https://schoolcrm-fzcv.onrender.com/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userId: lbUserId,
          password: lbPassword
        })
      });
      const authData = await authRes.json();
      if (!authRes.ok) {
        setErrors(p => ({
          ...p,
          userId: authData.message || "Login failed",
          password: ""
        }));
        triggerShake();
        setLoading(false);
        return;
      }
      if (authData.user.role !== "staff") {
        setErrors(p => ({
          ...p,
          userId: `Only staff can access LogBook. This ID belongs to "${authData.user.role}".`,
          password: ""
        }));
        triggerShake();
        setLoading(false);
        return;
      }
      const statusRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/logbook-approvals/status?staffUserId=${encodeURIComponent(lbUserId)}&className=${encodeURIComponent(className)}`);
      const statusData = await statusRes.json();
      if (statusData.status === "approved") {
        localStorage.setItem("token", authData.token);
        localStorage.setItem("role", authData.user.role);
        localStorage.setItem("userId", authData.user.userId);
        localStorage.setItem("logbookClass", className);
        onLogin();
        return;
      }
      const reqRes = await fetch("https://schoolcrm-fzcv.onrender.com/api/logbook-approvals/request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          staffUserId: lbUserId,
          className
        })
      });
      const reqData = await reqRes.json();
      if (!reqRes.ok) {
        setErrors(p => ({
          ...p,
          userId: reqData.message || "Could not send approval request",
          password: ""
        }));
        triggerShake();
        setLoading(false);
        return;
      }
      if (reqData.status === "approved") {
        localStorage.setItem("token", authData.token);
        localStorage.setItem("role", authData.user.role);
        localStorage.setItem("userId", authData.user.userId);
        localStorage.setItem("logbookClass", className);
        onLogin();
        return;
      }
      setApprovedStaffData(authData);
      setLoading(false);
      setWaitingApproval(true);
    } catch {
      setErrors(p => ({
        ...p,
        userId: "Server error. Is backend running?",
        password: ""
      }));
      triggerShake();
    } finally {
      setLoading(false);
    }
  };
  const handleApproved = () => {
    const className = buildClassName();
    localStorage.setItem("token", approvedStaffData.token);
    localStorage.setItem("role", approvedStaffData.user.role);
    localStorage.setItem("userId", approvedStaffData.user.userId);
    localStorage.setItem("logbookClass", className);
    onLogin();
  };
  if (waitingApproval) {
    return <WaitingApproval staffUserId={lbUserId} className={buildClassName()} onApproved={handleApproved} onCancel={() => {
      setWaitingApproval(false);
      setLoading(false);
    }} />;
  }
  return <>
      <div onClick={onClose} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(6px)",
      zIndex: 100
    }} />
      <div className={shake ? "shake-card" : ""} style={{
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      zIndex: 101,
      width: "100%",
      maxWidth: 420,
      background: "#4F6F52",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: 20,
      boxShadow: "0 30px 70px rgba(0,0,0,0.35)",
      padding: "28px 28px 24px"
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 18
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10
        }}>
            <div style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: "#1A4D2E",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            boxShadow: "0 0 18px rgba(0,0,0,0.25)"
          }}><BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
            <div>
              <div style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 16
            }}>LogBook Access</div>
              <div style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11
            }}>Staff credentials required</div>
            </div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: 8,
          width: 30,
          height: 30,
          cursor: "pointer",
          color: "#fff",
          fontSize: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
        </div>
        <div style={{
        height: 1,
        background: "rgba(255,255,255,0.15)",
        marginBottom: 18
      }} />

        {/* Class Selector */}
        <div style={{
        marginBottom: 14
      }}>
          <label style={{
          color: "rgba(255,255,255,0.75)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: 7
        }}>Select Class &amp; Section</label>
          <button onClick={() => {
          setShowSelector(true);
          setSelectorStep("class");
        }} style={{
          width: "100%",
          background: buildClassName() ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.08)",
          border: errors.class ? "1.5px solid #fff" : buildClassName() ? "1.5px solid rgba(255,255,255,0.5)" : "1.5px solid rgba(255,255,255,0.15)",
          borderRadius: 12,
          padding: "12px 14px",
          color: "#fff",
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 13,
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
            <span>{displayLabel}</span><span style={{
            fontSize: 12
          }}>▼</span>
          </button>
          {errors.class && <div style={{
          color: "#fff",
          fontSize: 12,
          marginTop: 5,
          fontWeight: 600
        }}>{errors.class}</div>}
          {buildClassName() && <div style={{
          marginTop: 8,
          padding: "8px 12px",
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
              <span style={{
            fontSize: 13
          }}>{advisorLoading ? "⏳" : advisorName ? <School className="inline w-[1em] h-[1em] align-[-0.125em]" /> : "ℹ"}</span>
              <span style={{
            fontSize: 12,
            color: "#fff"
          }}>
                {advisorLoading ? "Fetching class advisor..." : advisorName ? `Class Advisor: ${advisorName}` : "No advisor assigned for this class"}
              </span>
              <button onClick={resetSelector} style={{
            marginLeft: "auto",
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            fontSize: 14
          }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
            </div>}
        </div>

        {/* Staff ID */}
        <div style={{
        marginBottom: 14
      }}>
          <label style={{
          color: "rgba(255,255,255,0.75)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: 7
        }}>Staff ID</label>
          <input type="text" value={lbUserId} placeholder="e.g. STF-1042" onChange={e => {
          setLbUserId(e.target.value);
          setErrors(p => ({
            ...p,
            userId: ""
          }));
        }} onKeyDown={e => e.key === "Enter" && handleLogBookLogin()} style={{
          width: "100%",
          background: "rgba(255,255,255,0.1)",
          border: errors.userId ? "1.5px solid #fff" : "1.5px solid rgba(255,255,255,0.2)",
          borderRadius: 12,
          padding: "12px 14px",
          color: "#fff",
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 14,
          outline: "none",
          boxSizing: "border-box"
        }} />
          {errors.userId && <div style={{
          color: "#fff",
          fontSize: 12,
          marginTop: 5,
          fontWeight: 600
        }}>{errors.userId}</div>}
        </div>

        {/* Password */}
        <div style={{
        marginBottom: 20
      }}>
          <label style={{
          color: "rgba(255,255,255,0.75)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: 7
        }}>Password</label>
          <div style={{
          position: "relative"
        }}>
            <input type={showPwd ? "text" : "password"} value={lbPassword} placeholder="Enter your password" onChange={e => {
            setLbPassword(e.target.value);
            setErrors(p => ({
              ...p,
              password: ""
            }));
          }} onKeyDown={e => e.key === "Enter" && handleLogBookLogin()} style={{
            width: "100%",
            background: "rgba(255,255,255,0.1)",
            border: errors.password ? "1.5px solid #fff" : "1.5px solid rgba(255,255,255,0.2)",
            borderRadius: 12,
            padding: "12px 42px 12px 14px",
            color: "#fff",
            fontFamily: "'DM Sans',sans-serif",
            fontSize: 14,
            outline: "none",
            boxSizing: "border-box"
          }} />
            <button onClick={() => setShowPwd(!showPwd)} style={{
            position: "absolute",
            right: 13,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.7)",
            fontSize: 15
          }}>{showPwd ? <EyeOff className="inline w-[1em] h-[1em] align-[-0.125em]" /> : <Eye className="inline w-[1em] h-[1em] align-[-0.125em]" />}</button>
          </div>
          {errors.password && <div style={{
          color: "#fff",
          fontSize: 12,
          marginTop: 5,
          fontWeight: 600
        }}>{errors.password}</div>}
        </div>

        <button onClick={handleLogBookLogin} disabled={loading} style={{
        width: "100%",
        padding: "13px",
        borderRadius: 12,
        background: loading ? "rgba(255,255,255,0.25)" : "#1A4D2E",
        color: "#fff",
        fontFamily: "'DM Sans',sans-serif",
        fontWeight: 700,
        fontSize: 14,
        border: "none",
        cursor: loading ? "not-allowed" : "pointer",
        boxShadow: "0 4px 18px rgba(0,0,0,0.2)",
        letterSpacing: "0.03em",
        transition: "all 0.2s ease"
      }}>
          {loading ? "Verifying..." : "Open LogBook"}
        </button>

        <div style={{
        marginTop: 14,
        padding: "10px 14px",
        background: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 10,
        textAlign: "center"
      }}>
          <div style={{
          color: "rgba(255,255,255,0.85)",
          fontSize: 11
        }}>
            <BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /> LogBook requires <span style={{
            color: "#fff",
            fontWeight: 700
          }}>Staff</span> role &nbsp;+&nbsp; <span style={{
            color: "#fff",
            fontWeight: 700
          }}>Class Advisor</span> approval
          </div>
        </div>
      </div>

      {/* Class Selector Popup */}
      {showSelector && <>
          <div onClick={() => {
        setShowSelector(false);
        setSelectorStep("class");
      }} style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 200
      }} />
          <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        zIndex: 201,
        width: "92%",
        maxWidth: 460,
        background: "#4F6F52",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 20,
        padding: "22px",
        maxHeight: "85vh",
        overflowY: "auto",
        boxShadow: "0 32px 80px rgba(0,0,0,0.4)"
      }}>
            <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16
        }}>
              <div>
                <div style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 15
            }}>
                  {selectorStep === "class" && "Select Class"}
                  {selectorStep === "group" && `${selectedClass?.label} — Select Group`}
                  {selectorStep === "section" && "Select Section"}
                </div>
                {selectorStep !== "class" && <button onClick={() => {
              if (selectorStep === "section" && selectedClass?.isHS) setSelectorStep("group");else setSelectorStep("class");
            }} style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 12,
              cursor: "pointer",
              padding: 0,
              marginTop: 4,
              opacity: 0.85
            }}><ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back</button>}
              </div>
              <button onClick={() => {
            setShowSelector(false);
            setSelectorStep("class");
          }} style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: 28,
            height: 28,
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 16
          }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
            </div>

            {selectorStep === "class" && <div>
                <div style={{
            fontSize: 10,
            color: "rgba(255,255,255,0.6)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 8
          }}>Kindergarten</div>
                <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            marginBottom: 16
          }}>
                  {CLASS_STRUCTURE.filter(c => c.label === "LKG" || c.label === "UKG").map(cls => <button key={cls.label} onClick={() => {
              setSelectedClass(cls);
              setSelectedGroup(null);
              setSelectedSection(null);
              setSelectorStep("section");
            }} style={classBtnStyle(selectedClass?.label === cls.label)}>{cls.label}</button>)}
                </div>
                <div style={{
            fontSize: 10,
            color: "rgba(255,255,255,0.6)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 8
          }}>Class 1 – 10</div>
                <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(5,1fr)",
            gap: 6,
            marginBottom: 16
          }}>
                  {CLASS_STRUCTURE.filter(c => !c.isHS && c.label.startsWith("Class")).map(cls => <button key={cls.label} onClick={() => {
              setSelectedClass(cls);
              setSelectedGroup(null);
              setSelectedSection(null);
              setSelectorStep("section");
            }} style={classBtnStyle(selectedClass?.label === cls.label)}>{cls.label.replace("Class ", "")}</button>)}
                </div>
                <div style={{
            fontSize: 10,
            color: "rgba(255,255,255,0.6)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 8
          }}>Higher Secondary (11 &amp; 12)</div>
                <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6
          }}>
                  {CLASS_STRUCTURE.filter(c => c.isHS).map(cls => <button key={cls.label} onClick={() => {
              setSelectedClass(cls);
              setSelectedGroup(null);
              setSelectedSection(null);
              setSelectorStep("group");
            }} style={{
              ...classBtnStyle(selectedClass?.label === cls.label),
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)"
            }}>{cls.label} <GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>)}
                </div>
              </div>}

            {selectorStep === "group" && selectedClass?.isHS && <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 8
        }}>
                {selectedClass.groups.map(g => <button key={g.group} onClick={() => {
            setSelectedGroup(g.group);
            setSelectedSection(null);
            setSelectorStep("section");
          }} style={{
            background: selectedGroup === g.group ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
            border: selectedGroup === g.group ? "1.5px solid #fff" : "1px solid rgba(255,255,255,0.15)",
            borderRadius: 12,
            padding: "13px 16px",
            color: "#fff",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "'DM Sans',sans-serif"
          }}>
                    <span style={{
              fontSize: 18
            }}>{groupIcon(g.group)}</span>
                    <div>
                      <div style={{
                fontWeight: 600
              }}>{g.group}</div>
                      <div style={{
                fontSize: 10,
                color: "rgba(255,255,255,0.6)",
                marginTop: 2
              }}>Sections A &amp; B available</div>
                    </div>
                  </button>)}
              </div>}

            {selectorStep === "section" && selectedClass && <div>
                <div style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.7)",
            marginBottom: 14
          }}>
                  {selectedClass.label}{selectedGroup ? ` › ${selectedGroup}` : ""} — Choose a section
                </div>
                <div style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap"
          }}>
                  {(selectedClass.isHS ? selectedClass.groups.find(g => g.group === selectedGroup)?.sections : selectedClass.sections)?.map(sec => <button key={sec} onClick={() => handleSectionPick(sec)} style={{
              flex: 1,
              minWidth: 80,
              background: selectedSection === sec ? "#1A4D2E" : "rgba(255,255,255,0.1)",
              border: selectedSection === sec ? "1.5px solid #fff" : "1px solid rgba(255,255,255,0.15)",
              borderRadius: 12,
              padding: "16px 10px",
              color: "#fff",
              fontSize: 22,
              fontWeight: 700,
              cursor: "pointer",
              textAlign: "center",
              fontFamily: "'DM Sans',sans-serif"
            }}>
                      {sec}<div style={{
                fontSize: 10,
                fontWeight: 400,
                color: "rgba(255,255,255,0.75)",
                marginTop: 4
              }}>Section</div>
                    </button>)}
                </div>
              </div>}
          </div>
        </>}
    </>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN LOGIN PAGE
//  Full UI with #1A4D2E, #4F6F52, #E8DFCA, #F5EFE6 palette
//  Left panel text/labels/links use the gold gradient (#F3D583 → #E8C468)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
//  SCHOOL NAME SETTINGS MODAL — set from the ⚙ icon, top-right of the page
// ─────────────────────────────────────────────────────────────────────────────
function SchoolNameModal({
  currentName,
  onClose,
  onSave
}) {
  const [value, setValue] = useState(currentName);
  const [saved, setSaved] = useState(false);
  const handleSave = () => {
    if (!value.trim()) return;
    onSave(value.trim());
    setSaved(true);
    setTimeout(() => onClose(), 700);
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(26,45,33,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16
  }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
      background: "#F5EFE6",
      borderRadius: 20,
      padding: "28px 26px",
      width: "100%",
      maxWidth: 380,
      boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
      fontFamily: "'DM Sans',sans-serif",
      position: "relative"
    }}>
        <button onClick={onClose} aria-label="Close" style={{
        position: "absolute",
        top: 14,
        right: 14,
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "#1A4D2E",
        opacity: 0.6,
        display: "flex"
      }}>
          <FiX size={20} />
        </button>

        <div style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: "rgba(26,77,46,0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 14
      }}>
          <FiSettings size={20} color="#1A4D2E" />
        </div>

        <div style={{
        fontFamily: "'Playfair Display',serif",
        color: "#1A4D2E",
        fontSize: 20,
        marginBottom: 4
      }}>
          School Name Settings
        </div>
        <div style={{
        color: "#1A4D2E",
        fontSize: 12.5,
        opacity: 0.75,
        marginBottom: 18
      }}>
          This name is shown across the login page, dashboards, receipts and reports.
        </div>

        <label style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#1A4D2E",
        display: "block",
        marginBottom: 8
      }}>
          School Name
        </label>
        <input type="text" value={value} placeholder="e.g. Classora" onChange={e => setValue(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSave()} autoFocus style={{
        width: "100%",
        background: "#FFFFFF",
        border: "1.5px solid rgba(26,77,46,0.2)",
        borderRadius: 12,
        padding: "12px 14px",
        color: "#1A4D2E",
        fontFamily: "'DM Sans',sans-serif",
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
        marginBottom: 20
      }} />

        <button onClick={handleSave} style={{
        width: "100%",
        padding: "13px",
        borderRadius: 12,
        background: GOLD_GRADIENT,
        color: "#1A4D2E",
        fontFamily: "'DM Sans',sans-serif",
        fontWeight: 700,
        fontSize: 14,
        border: "none",
        cursor: "pointer",
        letterSpacing: "0.03em"
      }}>
          {saved ? "Saved" : "Save School Name"}
        </button>
      </div>
    </div>;
}
// ─────────────────────────────────────────────────────────────────────────────
//  FIT SCHOOL NAME — keeps the school name on a single line on the login page,
//  no matter how long it is, by shrinking the font size to fit the available
//  width instead of wrapping.
// ─────────────────────────────────────────────────────────────────────────────
function FitSchoolName({ text }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const MAX_FONT_SIZE = 28;
  const MIN_FONT_SIZE = 13;
  const [fontSize, setFontSize] = useState(MAX_FONT_SIZE);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const el = textRef.current;
    if (!container || !el) return;
    let size = MAX_FONT_SIZE;
    el.style.fontSize = `${size}px`;
    while (el.scrollWidth > container.clientWidth && size > MIN_FONT_SIZE) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
    setFontSize(size);
  }, [text]);

  return <div ref={containerRef} style={{
    width: "100%",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden"
  }}>
      <div ref={textRef} style={{
      fontFamily: "'Playfair Display',serif",
      color: "#1A4D2E",
      fontSize,
      marginBottom: 4,
      whiteSpace: "nowrap",
      lineHeight: 1.2
    }}>{text}</div>
    </div>;
}
export default function SchoolLoginPage() {
  const [selectedRole, setSelectedRole] = useState("principal");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    userId: "",
    password: ""
  });
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLogBookModal, setShowLogBookModal] = useState(false);
  const [showSchoolNameModal, setShowSchoolNameModal] = useState(false);
  const navigate = useNavigate();
  const cfg = roleConfig[selectedRole];
  const schoolName = useSchoolName();
  const handleRoleChange = role => {
    setSelectedRole(role);
    setUserId("");
    setPassword("");
    setErrors({
      userId: "",
      password: ""
    });
  };
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  PRINCIPAL LOGIN — validates against DB headmistress record
  // ─────────────────────────────────────────────────────────────────────────
  const handlePrincipalLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
      const result = await res.json();
      const hm = result.data;
      if (!hm) {
        setErrors({
          userId: "No principal account found. Please set up principal details first.",
          password: ""
        });
        triggerShake();
        setLoading(false);
        return;
      }
      const usernameMatch = userId.trim() === hm.username;
      const passwordMatch = password === hm.passwordDisplay;
      if (usernameMatch && passwordMatch) {
        localStorage.setItem("role", "principal");
        localStorage.setItem("userId", hm.username);
        navigate("/dashboard");
      } else {
        setErrors({
          userId: "Invalid Principal username or password.",
          password: ""
        });
        triggerShake();
      }
    } catch {
      setErrors({
        userId: "Server error. Is backend running?",
        password: ""
      });
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ADMIN LOGIN — hardcoded (unchanged)
  // ─────────────────────────────────────────────────────────────────────────
  const handleAdminLogin = () => {
    if (userId === ADMIN_DEFAULT.userId && password === ADMIN_DEFAULT.password) {
      localStorage.setItem("role", "admin");
      localStorage.setItem("userId", userId);
      navigate("/admin");
    } else {
      setErrors({
        userId: "Invalid Admin ID or Password.",
        password: ""
      });
      triggerShake();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  STAFF LOGIN — via backend auth API (unchanged)
  // ─────────────────────────────────────────────────────────────────────────
  const handleStaffLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userId,
          password
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setErrors({
          userId: data.message || "Login failed",
          password: ""
        });
        triggerShake();
        setLoading(false);
        return;
      }
      if (data.user.role !== "staff") {
        setErrors({
          userId: `This ID belongs to "${data.user.role}". Please select the correct role tab.`,
          password: ""
        });
        triggerShake();
        setLoading(false);
        return;
      }
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("userId", data.user.userId);
      navigate("/staffdetails");
    } catch {
      setErrors({
        userId: "Server error. Is backend running?",
        password: ""
      });
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  UNIFIED HANDLER
  // ─────────────────────────────────────────────────────────────────────────
  const handleLogin = async () => {
    if (!userId.trim()) {
      setErrors({
        userId: "Please enter your ID",
        password: ""
      });
      triggerShake();
      return;
    }
    if (!password.trim()) {
      setErrors({
        userId: "",
        password: "Please enter your password"
      });
      triggerShake();
      return;
    }
    if (selectedRole === "principal") {
      await handlePrincipalLogin();
      return;
    }
    if (selectedRole === "admin") {
      handleAdminLogin();
      return;
    }
    if (selectedRole === "staff") {
      await handleStaffLogin();
      return;
    }
  };
  return <>
      <div style={{
      fontFamily: "'DM Sans',sans-serif",
      background: "#F5EFE6",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
      position: "relative",
      overflow: "hidden"
    }}>
        {/* ── SCHOOL NAME SETTINGS ICON ─────────────────────────────── */}
        <button onClick={() => setShowSchoolNameModal(true)} title="School name settings" aria-label="School name settings" style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 20,
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "#FFFFFF",
        border: "1px solid rgba(26,77,46,0.15)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "#1A4D2E",
        transition: "all 0.2s ease"
      }} onMouseEnter={e => {
        e.currentTarget.style.background = "rgba(26,77,46,0.06)";
        e.currentTarget.style.transform = "rotate(45deg)";
      }} onMouseLeave={e => {
        e.currentTarget.style.background = "#FFFFFF";
        e.currentTarget.style.transform = "rotate(0deg)";
      }}>
          <FiSettings size={18} />
        </button>

        {/* Background glows */}
        <div style={{
        position: "absolute",
        top: -150,
        right: -150,
        width: 500,
        height: 500,
        borderRadius: "50%",
        background: "radial-gradient(circle,rgba(26,77,46,0.06) 0%,transparent 70%)",
        pointerEvents: "none"
      }} />
        <div style={{
        position: "absolute",
        bottom: -100,
        left: -100,
        width: 400,
        height: 400,
        borderRadius: "50%",
        background: "radial-gradient(circle,rgba(79,111,82,0.08) 0%,transparent 70%)",
        pointerEvents: "none"
      }} />

        {/* ── SPLIT CARD ─────────────────────────────────────────────── */}
        <div className={`split-card ${shake ? "shake-card" : ""}`} style={{
        display: "flex",
        background: "#F5EFE6",
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: 24,
        boxShadow: "0 25px 60px rgba(0,0,0,0.12)",
        width: "100%",
        maxWidth: 880,
        overflow: "hidden"
      }}>

          {/* ── LEFT PANEL: solid #1A4D2E — Username / Password / Sign In ─ */}
          <div className="split-left" style={{
          flex: "1 1 50%",
          padding: "40px 36px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          background: "#1A4D2E"
        }}>
            <div style={{
            marginBottom: 22
          }}>
              <div className="gold-grad-text" style={{
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 4
            }}>Welcome back</div>
              <div className="gold-grad-text" style={{
              fontSize: 13
            }}>
                Sign in as <span className="gold-grad-text" style={{
                fontWeight: 700
              }}>{cfg.title}</span> to continue
              </div>
            </div>

            {/* User ID field */}
            <div style={{
            marginBottom: 14
          }}>
              <label className="gold-grad-text" style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8
            }}>{cfg.label}</label>
              <input type="text" value={userId} placeholder={cfg.placeholder} onChange={e => {
              setUserId(e.target.value);
              setErrors(p => ({
                ...p,
                userId: ""
              }));
            }} onKeyDown={e => e.key === "Enter" && handleLogin()} style={{
              width: "100%",
              background: "rgba(255,255,255,0.08)",
              border: errors.userId ? "1.5px solid #E8DFCA" : "1.5px solid rgba(255,255,255,0.15)",
              borderRadius: 12,
              padding: "13px 16px",
              color: "#E8DFCA",
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box"
            }} />
              {errors.userId && <div className="gold-grad-text" style={{
              fontSize: 12,
              marginTop: 6,
              fontWeight: 600
            }}>{errors.userId}</div>}
            </div>

            {/* Password field */}
            <div style={{
            marginBottom: 10
          }}>
              <label className="gold-grad-text" style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 8
            }}>Password</label>
              <div style={{
              position: "relative"
            }}>
                <input type={showPassword ? "text" : "password"} value={password} placeholder="Enter your password" onChange={e => {
                setPassword(e.target.value);
                setErrors(p => ({
                  ...p,
                  password: ""
                }));
              }} onKeyDown={e => e.key === "Enter" && handleLogin()} style={{
                width: "100%",
                background: "rgba(255,255,255,0.08)",
                border: errors.password ? "1.5px solid #E8DFCA" : "1.5px solid rgba(255,255,255,0.15)",
                borderRadius: 12,
                padding: "13px 44px 13px 16px",
                color: "#E8DFCA",
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 14,
                outline: "none",
                boxSizing: "border-box"
              }} />
                <button onClick={() => setShowPassword(!showPassword)} style={{
                position: "absolute",
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(232,223,202,0.6)",
                fontSize: 16
              }}>
                  {showPassword ? <EyeOff className="inline w-[1em] h-[1em] align-[-0.125em]" /> : <Eye className="inline w-[1em] h-[1em] align-[-0.125em]" />}
                </button>
              </div>
              {errors.password && <div className="gold-grad-text" style={{
              fontSize: 12,
              marginTop: 6,
              fontWeight: 600
            }}>{errors.password}</div>}
            </div>

          

            {/* Sign In Button */}
            <button onClick={handleLogin} disabled={loading} style={{
            width: "100%",
            padding: "14px",
            borderRadius: 12,
            background: loading ? "rgba(232,223,202,0.3)" : GOLD_GRADIENT,
            color: "#1A4D2E",
            fontFamily: "'DM Sans',sans-serif",
            fontWeight: 700,
            fontSize: 15,
            border: "none",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            letterSpacing: "0.03em",
            transition: "all 0.2s ease"
          }} onMouseEnter={e => {
            if (!loading) {
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 8px 28px rgba(0,0,0,0.25)";
            }
          }} onMouseLeave={e => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 20px rgba(0,0,0,0.2)";
          }}>
              {loading ? "Signing in..." : "Sign In to Portal"}
            </button>

            {/* Info Box */}
            <div style={{
            background: "rgba(232,223,202,0.08)",
            border: "1px solid rgba(232,223,202,0.15)",
            borderRadius: 12,
            padding: "12px 16px",
            textAlign: "center",
            marginTop: 16
          }}>
              {selectedRole === "principal" && <div className="gold-grad-text" style={{
              fontSize: 11,
              marginBottom: 6,
              fontWeight: 600
            }}>
                  <Crown className="inline w-[1em] h-[1em] align-[-0.125em]" /> Use the username & password set in Principal Details
                </div>}
              {selectedRole === "admin" && <div className="gold-grad-text" style={{
              fontSize: 11,
              marginBottom: 6,
              fontWeight: 600
            }}>
                  <KeyRound className="inline w-[1em] h-[1em] align-[-0.125em]" /> Default: ADM-0301 / admin@123
                </div>}
              <div className="gold-grad-text" style={{
              fontSize: 12
            }}>
                Logging in as <span className="gold-grad-text" style={{
                fontWeight: 700
              }}>{cfg.title}</span> — {cfg.info}
              </div>
            </div>

            <div className="gold-grad-text" style={{
            textAlign: "center",
            marginTop: 22,
            fontSize: 11
          }}>
              © 2026 {schoolName} CRM · Secure Login · All rights reserved
            </div>
          </div>

          {/* Divider */}
          <div className="split-divider" style={{
          width: 1,
          background: "rgba(232,223,202,0.2)"
        }} />

          {/* ── RIGHT PANEL: solid #4F6F52 — ALL TEXT IN #E8DFCA ─ */}
          <div className="split-right" style={{
          flex: "1 1 50%",
          padding: "40px 36px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          background: "#F5EFE6"
        }}>

            {/* Logo / Title - All text in #E8DFCA */}
            <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 26
          }}>
              <div style={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              background: "#E8DFCA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              marginBottom: 14,
              boxShadow: "0 0 30px rgba(0,0,0,0.08)"
            }}><School className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
              <FitSchoolName text={schoolName} />
              <div style={{
              color: "#1A4D2E",
              fontSize: 13,
              marginBottom: 10,
              opacity: 0.85
            }}>School Management System</div>
              <span style={{
              display: "inline-block",
              padding: "3px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              background: "rgba(26,77,46,0.08)",
              color: "#1A4D2E",
              border: "1px solid rgba(26,77,46,0.25)",
              letterSpacing: "0.06em"
            }}>SECURE PORTAL</span>
            </div>

            {/* Role Tabs - All text in #E8DFCA */}
            <div style={{
            marginBottom: 22
          }}>
              <div style={{
              color: "#1A4D2E",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 10,
              textAlign: "center"
            }}>Login As</div>
              <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 8
            }}>
                {Object.entries(roleConfig).map(([key, r]) => <button key={key} onClick={() => handleRoleChange(key)} style={{
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                border: selectedRole === key ? "1.5px solid #1A4D2E" : "1px solid rgba(26,77,46,0.15)",
                background: selectedRole === key ? "rgba(26,77,46,0.06)" : "#FFFFFF",
                color: "#1A4D2E",
                borderRadius: 12,
                padding: "12px 14px",
                fontSize: 14,
                fontWeight: selectedRole === key ? 700 : 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 10,
                transition: "all 0.2s ease",
                boxShadow: selectedRole === key ? "0 4px 14px rgba(0,0,0,0.08)" : "none"
              }}>
                    <span style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: ROLE_ICON_BG[key],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  flexShrink: 0
                }}>{r.icon}</span>
                    <div style={{
                  textAlign: "left"
                }}>
                      <div>{r.title}</div>
                      <div style={{
                    fontSize: 11,
                    fontWeight: 400,
                    color: "#1A4D2E",
                    opacity: 0.65
                  }}>{r.info}</div>
                    </div>
                  </button>)}
              </div>
            </div>

            {/* LogBook Button */}
            <button onClick={() => setShowLogBookModal(true)} style={{
            width: "100%",
            padding: "13px",
            borderRadius: 12,
            background: "#FFFFFF",
            border: "1.5px solid rgba(26,77,46,0.15)",
            color: "#1A4D2E",
            fontFamily: "'DM Sans',sans-serif",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            letterSpacing: "0.03em",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = "#1A4D2E";
            e.currentTarget.style.background = "rgba(26,77,46,0.04)";
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "rgba(26,77,46,0.15)";
            e.currentTarget.style.background = "#FFFFFF";
          }}>
              <span style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              background: GOLD_GRADIENT,
              display: "inline-block",
              flexShrink: 0
            }}></span>
              LogBook — Attendance Portal
            </button>
          </div>
        </div>

        <style>{`
          @keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}
          .shake-card{animation:shake 0.4s ease}
          input:focus{border-color:#E8DFCA!important;box-shadow:0 0 0 3px rgba(232,223,202,0.2);background:rgba(255,255,255,0.12)!important}
          .split-left input::placeholder{color:rgba(232,223,202,0.55);opacity:1}
          .gold-grad-text{background:linear-gradient(135deg, #F3D583, #E8C468);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent;}

          @media (max-width: 760px) {
            .split-card { flex-direction: column !important; max-width: 440px !important; }
            .split-divider { width: 100% !important; height: 1px !important; }
            .split-right { order: -1; }
          }
        `}</style>
      </div>

      {showLogBookModal && <LogBookModal onClose={() => setShowLogBookModal(false)} onLogin={() => {
      setShowLogBookModal(false);
      navigate("/logbook");
    }} />}

      {showSchoolNameModal && <SchoolNameModal currentName={schoolName} onClose={() => setShowSchoolNameModal(false)} onSave={name => saveSchoolName(name)} />}
    </>;
}